import React, { useEffect, useState, useMemo } from "react";
import {
  TableContainer,
  Container,
  Grid,
  Box,
  Paper,
  LinearProgress,
  Typography,
  Select,
  MenuItem,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import {
  DataGrid,
  gridColumnVisibilityModelSelector,
  useGridApiContext,
  useGridRootProps,
  isLeaf,
  gridColumnLookupSelector,
  useGridSelector,
  GridColumnsPanel,
} from "@mui/x-data-grid";
import WarningIcon from "@mui/icons-material/Warning";
import LockIcon from "@mui/icons-material/Lock";
import { Header } from "../../../components/global/Header";
import FormListToolbar from "../../../components/global/Toolbar";
import InputLabel from "@mui/material/InputLabel";
import FormControl from "@mui/material/FormControl";
import Checkbox from "@mui/material/Checkbox";
import FormControlLabel from "@mui/material/FormControlLabel";
import Slider from "@mui/material/Slider";
import Stack from "@mui/material/Stack";
import useSWR from "swr";
import ProductionStatusDetails from "./ProductionStatusDetails";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Tooltip from "@mui/material/Tooltip";
import OnlinePredictionIcon from "@mui/icons-material/OnlinePrediction";
import { GridCellParams, GridColDef } from "@mui/x-data-grid";
import Drawer from "@mui/material/Drawer";

const EpisodeGrid = () => {
  const [threshold, setThreshold] = useState(0.7);
  const [selectedCategoryItem, setSelectedCategoryItem] = useState(null);
  const [selectedProduction, setSelectedProduction] = useState(null);
  const [selectedColumn, setSelectedColumn] = useState(null);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedItemName, setSelectedItemName] = useState(null);
  const [selectedBudget, setSelectedBudget] = useState(null);

  const { data: productions, error: productionsError } = useSWR("/productions");
  const { data: categoryItems, error: categoryError } = useSWR(
    selectedProduction?.id
      ? `/production-category-item/of-production/${selectedProduction.id}`
      : null
  );

  const { data: itemsDetails, error: detailsError } = useSWR(
    selectedProduction?.id && categoryItems
      ? `/expense/test/${selectedProduction.id}/${categoryItems.map((item) => item.id).join(",")}`
      : null,
    async () => {
      const token = localStorage.getItem("token");

      try {
        const itemRequests = categoryItems.map((item) =>
          fetch(
            `${process.env.REACT_APP_PROTOCOL === 'https' || !process.env.REACT_APP_BACKEND_PORT ? '' : `http://${process.env.REACT_APP_HOST}:${process.env.REACT_APP_BACKEND_PORT}`}/api/expense/test/${selectedProduction.id}/${item.id}`,
            {
              headers: {
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
              },
            }
          )
            .then((res) => {
              if (!res.ok) {
                throw new Error(
                  `Error fetching item ${item.id}: ${res.statusText}`
                );
              }
              return res.json();
            })
            .catch((error) => {
              console.error(`Error fetching item ${item.id}:`, error);
              return null; // Return null for this item if the fetch fails
            })
        );

        const results = await Promise.all(itemRequests);

        // Filter out null responses to avoid processing invalid data
        return results.filter((item) => item !== null);
      } catch (error) {
        console.error("Error fetching items:", error);
        throw error; // Let SWR handle the error
      }
    },
    { refreshInterval: 3700 }
  );

  useEffect(() => {
    const storedThreshold = localStorage.getItem("threshold");
    if (storedThreshold) {
      setThreshold(parseFloat(storedThreshold));
    }
  }, []);

  useEffect(() => {
    const savedProduction = localStorage.getItem("selectedProduction");
    if (savedProduction && productions) {
      const parsedProduction = JSON.parse(savedProduction);
      const production = productions.find((p) => p.id === parsedProduction.id);
      if (production) {
        setSelectedProduction(production);
      }
    }
  }, [productions]);

  // Save selected production to localStorage whenever it changes
  useEffect(() => {
    if (selectedProduction) {
      localStorage.setItem(
        "selectedProduction",
        JSON.stringify(selectedProduction)
      );
    }
  }, [selectedProduction]);

  const handleCellClick = (params) => {
    const episodeData = params.value;
    if (!episodeData) {
      return;
    }
    if (params.formattedValue.amount === null) {
      return;
    }

    setSelectedItem(params.row.item_db_id);
    setSelectedColumn(
      Number(
        params.field
          .split("")
          .filter((char) => /\d/.test(char))
          .join("")
      )
    );
    setSelectedItemName(params.row.item);
    setSelectedBudget(params.row.budget);
  };

  const handlePanelClose = () => {
    setSelectedItem(null);
    setSelectedColumn(null);
    setSelectedItemName(null);
  };

  const handleProductionChange = (event) => {
    setSelectedProduction(productions.find((p) => p.id === event.target.value));
    setThreshold(0.7);
    localStorage.removeItem("threshold");
  };

  const handleThresholdChange = (_, newValue) => {
    setThreshold(newValue);
    localStorage.setItem("threshold", newValue.toString());
  };

  const { rows, columnGroupingModel } = useMemo(() => {
    if (!selectedProduction || !categoryItems || !itemsDetails)
      return { columns: [], rows: [], columnGroupingModel: [] };

    const maxEpisodes = selectedProduction.no_of_episodes;

    const episodeColumns = Array.from({ length: maxEpisodes }, (_, i) => ({
      field: `episode-${i + 1}`,
      headerName: `Episode ${i + 1}`,
      width: 120,
      renderCell: (params) => {
        const value = params.value;

        if (value && !isNaN(value)) {
          const formattedAmount = new Intl.NumberFormat("en-IE", {
            style: "currency",
            currency: "EUR",
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          }).format(parseFloat(value));
          return formattedAmount;
        }
        return "";
      },
    }));

    const gridRows = categoryItems.map((item) => {
      const itemDetailsList = itemsDetails
        .flat()
        .filter((detail) => detail.pci_id === item.id);

      const episodeData = Object.fromEntries(
        Array.from({ length: maxEpisodes }, (_, i) => [
          `episode-${i + 1}`,
          { amount: null, expPred: false, maxEpAm: 0 },
        ])
      );

      let budget = "";

      const categoryItem = categoryItems.find(
        (category) => category.id === item.id
      );

      if (categoryItem) {
        budget = categoryItem.pci_amount;
      }

      itemDetailsList.forEach((itemDetail) => {
        itemDetail.exp_assigned_episodes.forEach((assignedEpisode) => {
          const episodeKey = `episode-${assignedEpisode}`;

          if (itemDetail.exp_pred === true) {
            episodeData[episodeKey].expPred = true;
          }

          const expenses = itemDetail.expense_details.filter(
            (expense) => expense.exp_d_episode === assignedEpisode
          );

          const totalExpense = expenses.reduce(
            (sum, expense) => sum + (expense.exp_d_amount || 0),
            0
          );

          episodeData[episodeKey].amount += totalExpense;

          if (itemDetail.exp_assigned_episodes?.length === maxEpisodes) {
            episodeData[episodeKey].maxEpAm += totalExpense;
          }
        });
      });

      const totalCost = Object.values(episodeData).reduce(
        (sum, ep) => sum + (ep.amount || 0),
        0
      );
      const validEpisodeCount = Object.values(episodeData).filter(
        (ep) => ep.amount !== null
      ).length;
      const averageCost =
        validEpisodeCount > 0 ? totalCost / validEpisodeCount : 0;

      return {
        item_db_id: item.id,
        id: item.id,
        item: item.pci_descr,
        budget,
        budgetTotal: budget * maxEpisodes,
        average: averageCost,
        totalItemActualCost: totalCost,
        ...Object.fromEntries(
          Object.entries(episodeData).map(([key, value]) => [
            key,
            {
              amount: value.amount,
              exp_pred: value.expPred,
              maxEpAm: value.maxEpAm,
            },
          ])
        ),
      };
    });

    const accumulatedBudget = categoryItems.reduce(
      (sum, item) => sum + (parseFloat(item.pci_amount) || 0),
      0
    );

    const totalItemActualCostAllRows = gridRows.reduce(
      (sum, row) => sum + (parseFloat(row.totalItemActualCost) || 0),
      0
    );

    const columnGroupingModel = [
      {
        groupId: "unamortized",
        headerAlign: "center",
        headerName: "Αναπόσβεστη Αξία",
        description: "",
        children: [
          {
            groupId: "total",
            headerAlign: "center",
            headerName: "Σύνολο",
            children: [{ field: "item" }],
          },
          {
            groupId: "budget",
            headerName: (() => {
              return new Intl.NumberFormat("en-IE", {
                style: "currency",
                currency: "EUR",
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }).format(accumulatedBudget);
            })(),
            description: "Total Budget",
            headerAlign: "right",
            children: [{ field: "budget" }],
          },
          {
            groupId: "average",
            headerName: "",
            description: "",
            headerAlign: "right",
            children: [{ field: "average" }],
          },
        ],
      },
      {
        groupId: `Ep. ${maxEpisodes + 1}`,
        headerAlign: "right",
        headerName: "",
        description: "",
        children: [
          {
            groupId: "budgetTotal",
            headerAlign: "right",
            headerName: new Intl.NumberFormat("en-IE", {
              style: "currency",
              currency: "EUR",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(accumulatedBudget * maxEpisodes),
            description: "Total Budget",
            children: [{ field: "budgetTotal" }],
          },
        ],
      },
      {
        groupId: `Ep. ${maxEpisodes + 2}`,
        headerAlign: "right",
        headerName: "",
        description: "",
        children: [
          {
            groupId: "totalItemActualCost",
            headerAlign: "right",
            headerName: new Intl.NumberFormat("en-IE", {
              style: "currency",
              currency: "EUR",
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            }).format(totalItemActualCostAllRows),
            description: "Total Items",
            children: [{ field: "totalItemActualCost" }],
          },
        ],
      },
      ...Array.from({ length: maxEpisodes }, (_, i) => {
        const episodeNumber = i + 1;
        const episodeSum = gridRows.reduce(
          (sum, row) => sum + parseFloat(row[`episode-${episodeNumber}`]?.amount || 0),
          0
        );

        const totalOfFullAssigned = gridRows.reduce(
          (sum, row) =>
            sum + (parseFloat(row[`episode-${episodeNumber}`]?.maxEpAm) || 0),
          0
        );

        const projectionValue = totalOfFullAssigned * (maxEpisodes - i); //* (maxEpisodes - i)
        const formattedProjection = new Intl.NumberFormat("en-IE", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(projectionValue);

        const formattedEpisodeSum = new Intl.NumberFormat("en-IE", {
          style: "currency",
          currency: "EUR",
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }).format(episodeSum);

        const isExceeded = episodeSum > accumulatedBudget;
        const episodeSumStyle = isExceeded
          ? { color: "red" }
          : { color: "#2bc12b" };

        return {
          groupId: `Ep. ${episodeNumber}`,
          headerAlign: "right",
          headerName: formattedProjection,
          description: "",
          children: [
            {
              groupId: `Episode ${episodeNumber}`,
              headerAlign: "right",
              headerName: (
                <span style={episodeSumStyle}>{formattedEpisodeSum}</span>
              ),
              description: ` `,
              children: [{ field: `episode-${episodeNumber}` }],
            },
          ],
        };
      }),
    ];

    return {
      columns: [
        { field: "item", headerName: "Στοιχεία", width: 200 },
        { field: "budget", headerName: "Προϋπολογισμός", width: 150 },
        ...episodeColumns,
      ],
      rows: [...gridRows],
      columnGroupingModel,
    };
  }, [selectedProduction, categoryItems, itemsDetails]);

  const isLoading = selectedProduction && (!categoryItems || !itemsDetails);

  if (productionsError || categoryError || detailsError) {
    return <Typography>Error loading data.</Typography>;
  }

  const getColumnGroupLeaves = (group, callback) => {
    group.children.forEach((child) => {
      if (isLeaf(child)) {
        callback(child.field);
      } else {
        getColumnGroupLeaves(child, callback);
      }
    });
  };

  function ColumnGroup({ group, columnLookup, apiRef, columnVisibilityModel }) {
    const leaves = React.useMemo(() => {
      const fields = [];
      getColumnGroupLeaves(group, (field) => fields.push(field));
      return fields;
    }, [group]);

    const { isGroupChecked, isGroupIndeterminate } = React.useMemo(() => {
      return {
        isGroupChecked: leaves.every(
          (field) => columnVisibilityModel[field] !== false
        ),
        isGroupIndeterminate:
          leaves.some((field) => columnVisibilityModel[field] === false) &&
          !leaves.every((field) => columnVisibilityModel[field] === false),
      };
    }, [columnVisibilityModel, leaves]);

    const toggleColumnGroup = (checked) => {
      const newColumnVisibilityModel = {
        ...columnVisibilityModel,
      };
      getColumnGroupLeaves(group, (field) => {
        newColumnVisibilityModel[field] = checked;
      });
      apiRef.current.setColumnVisibilityModel(newColumnVisibilityModel);
    };

    const toggleColumn = (field, checked) => {
      apiRef.current.setColumnVisibility(field, checked);
    };

    return (
      <div>
        <FormControlLabel
          control={
            <Checkbox
              checked={isGroupChecked}
              indeterminate={isGroupIndeterminate}
              size="small"
              sx={{ p: 1 }}
            />
          }
          label={group.headerName ?? group.groupId}
          onChange={(_, newValue) => toggleColumnGroup(newValue)}
        />
        <Box sx={{ pl: 3.5 }}>
          {group.children.map((child) => {
            return isLeaf(child) ? (
              <Stack direction="row" key={child.field}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={columnVisibilityModel[child.field] !== false}
                      size="small"
                      sx={{ p: 1 }}
                    />
                  }
                  label={columnLookup[child.field].headerName ?? child.field}
                  onChange={(_, newValue) =>
                    toggleColumn(child.field, newValue)
                  }
                />
              </Stack>
            ) : (
              <ColumnGroup
                group={child}
                columnLookup={columnLookup}
                key={child.groupId}
                apiRef={apiRef}
                columnVisibilityModel={columnVisibilityModel}
              />
            );
          })}
        </Box>
      </div>
    );
  }

  function ColumnsPanel(props) {
    const apiRef = useGridApiContext();
    const rootProps = useGridRootProps();
    const columnLookup = useGridSelector(apiRef, gridColumnLookupSelector);
    const columnVisibilityModel = useGridSelector(
      apiRef,
      gridColumnVisibilityModelSelector
    );

    const columnGroupingModel = rootProps.columnGroupingModel;

    if (!columnGroupingModel) {
      return <GridColumnsPanel {...props} />;
    }

    return (
      <Box sx={{ px: 2, py: 0.5 }}>
        {columnGroupingModel.map((group) => (
          <ColumnGroup
            group={group}
            columnLookup={columnLookup}
            columnVisibilityModel={columnVisibilityModel}
            key={group.groupId}
            apiRef={apiRef}
          />
        ))}
      </Box>
    );
  }

  return (
    <Box m="20px">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Header
          title="Category Items"
          module="Productions"
          subtitle="List of category items"
        />
      </Box>

      <Grid container spacing={2}>
        <Grid item xs={selectedCategoryItem ? 8 : 12}>
          <Box m="8px 0 0 0" height="80vh">
            <Paper style={{ padding: "1rem", height: "82vh", width: "100%" }}>
              <FormControl fullWidth size="small">
                <InputLabel id="prod-label">Παραγωγή</InputLabel>
                <Select
                  labelId="prod-label"
                  id="prod-id"
                  label="Production"
                  fullWidth
                  size="small"
                  maxWidth={"100%"}
                  value={selectedProduction?.id || ""}
                  onChange={handleProductionChange}
                  style={{ width: "20%", marginBottom: "16px" }}
                >
                  {productions?.map((production) => (
                    <MenuItem key={production.id} value={production.id}>
                      {production.p_descr}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <Box display="flex" alignItems="center" mb={2}>
                <Typography>Όριο:</Typography>
                <Slider
                  value={threshold}
                  min={0.1}
                  max={1}
                  step={0.05}
                  onChange={handleThresholdChange}
                  valueLabelDisplay="auto"
                  style={{ marginLeft: "16px", width: "200px" }}
                />
                <Typography>{Math.round(threshold * 100)}%</Typography>
              </Box>

              {/* Show loading indicator only after a production is selected */}
              {isLoading && <LinearProgress />}

              {/*If no prod. is selected yet*/}
              {!selectedProduction && (
                <DataGrid
                  sx={{
                    height: "70vh",
                    "& .MuiDataGrid-cell": {
                      borderRight: "1px solid #ddd",
                      borderBottom: "none",
                    },
                    "& .MuiDataGrid-columnHeader": {
                      borderRight: "1px solid #ddd",
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontWeight: "bold",
                    },
                  }}
                  rows={rows}
                  columns={[
                    {
                      field: "item",
                      headerName: "Στοιχεία",
                      flex: 1,
                      minWidth: 160,
                      maxWidth: 270,
                    },
                    {
                      field: "budget",
                      headerName: "Προϋπολογισμός",
                      flex: 1,
                      minWidth: 100,
                      maxWidth: 160,
                    },
                    {
                      field: "budgetTotal",
                      headerName: "Προϋπολογισμός",
                      flex: 1,
                      minWidth: 100,
                      maxWidth: 160,
                    },
                    {
                      field: "episode-1",
                      headerName: "Επεισόδια",
                      flex: 1,
                      minWidth: 100,
                      maxWidth: 160,
                    },
                  ]}
                  disableSelectionOnClick
                  style={{ overflow: "hidden", width: "100%" }}
                />
              )}

              {/*Data Grid*/}
              {selectedProduction && categoryItems && !isLoading && (
                <DataGrid
                  sx={{
                    height: "66vh",
                    "& .MuiDataGrid-cell": {
                      borderRight: "1px solid #ddd",
                      borderBottom: "none",
                    },
                    "& .MuiDataGrid-columnHeader": {
                      borderRight: "1px solid #ddd",
                    },
                    "& .MuiDataGrid-columnHeaderTitle": {
                      fontWeight: "bold",
                    },
                  }}
                  columnGroupingModel={columnGroupingModel}
                  slots={{ columnsPanel: ColumnsPanel }}
                  rows={rows}
                  columns={[
                    {
                      field: "id",
                    },
                    {
                      field: "item",
                      headerAlign: "center",
                      headerName: "Στοιχείο",
                      flex: 1,
                      minWidth: 300,
                      maxWidth: 500,
                      editable: false,
                    },
                    {
                      field: "budget",
                      headerAlign: "center",
                      headerName: "Προϋπολογισμός",
                      flex: 1,
                      minWidth: 160,
                      maxWidth: 190,
                      renderCell: (params) => {
                        const value = parseFloat(params.value);
                        if (!value) return null;

                        const formattedAmount = new Intl.NumberFormat("en-IE", {
                          style: "currency",
                          currency: "EUR",
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(value);

                        return (
                          <span
                            style={{
                              display: "inline-block",
                              textAlign: "right",
                              width: "100%",
                            }}
                          >
                            {formattedAmount}
                          </span>
                        );
                      },
                    },
                    {
                      field: "average",
                      headerAlign: "center",
                      headerName: "Μέσος Όρος",
                      flex: 1,
                      minWidth: 150,
                      maxWidth: 200,
                      renderCell: (params) => {
                        const formattedAverage = params?.row?.average
                          ? new Intl.NumberFormat("en-IE", {
                              style: "currency",
                              currency: "EUR",
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(params.row.average)
                          : null;
                        return (
                          <span
                            style={{
                              display: "inline-block",
                              textAlign: "right",
                              width: "100%",
                            }}
                          >
                            {formattedAverage}
                          </span>
                        );
                      },
                    },
                    ...Array.from(
                      { length: selectedProduction.no_of_episodes },
                      (_, i) => {
                        const episodeNumber = i + 1;
                        const lockedEpisodes = Array.isArray(selectedProduction?.locked_episodes) 
                          ? selectedProduction.locked_episodes 
                          : [];
                        const isLocked = lockedEpisodes.includes(episodeNumber);
                        
                        return {
                          field: `episode-${episodeNumber}`,
                          headerAlign: "center",
                          headerName: (
                            <span style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '4px' }}>
                              {isLocked && (
                                <LockIcon 
                                  sx={{ 
                                    fontSize: 16, 
                                    color: '#ff9800',
                                  }} 
                                  title="Κλειδωμένο Επεισόδιο"
                                />
                              )}
                              <span>E-{episodeNumber}</span>
                            </span>
                          ),
                          width: 120,
                        sortComparator: (v1, v2) => {
                          const a = v1?.amount || 0;
                          const b = v2?.amount || 0;
                          return a - b;
                        },
                        renderCell: (params) => {
                          const episodeData = params.value; // This is now { amount, exp_pred }
                          if (!episodeData) return "";

                          const { amount, exp_pred } = episodeData;
                          const budget = parseFloat(params.row.budget);
                          const isAboveBudget = amount > budget;
                          const isBelowThreshold =
                            !isAboveBudget && amount < budget * threshold;

                          const formattedAmount =
                            amount !== null
                              ? new Intl.NumberFormat("en-IE", {
                                  style: "currency",
                                  currency: "EUR",
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                }).format(amount)
                              : null;

                          return (
                            <div
                              style={{
                                marginRight: "auto",
                                position: "relative",
                                display: "flex",
                                justifyContent: "flex-end",
                                alignItems: "center",
                                color: isAboveBudget
                                  ? "red"
                                  : isBelowThreshold
                                    ? "orange"
                                    : "inherit",
                                fontWeight:
                                  isAboveBudget || isBelowThreshold
                                    ? "bold"
                                    : "inherit",
                              }}
                            >
                              {exp_pred && (
                                <div
                                  style={{
                                    position: "absolute",
                                    justifyContent: "flex-start",
                                    display: "flex",
                                    top: 0,
                                    right: 98.5,
                                    width: 0,
                                    height: 0,
                                    borderRight: "10px solid transparent",
                                    borderTop: "10px solid green",
                                    zIndex: 1,
                                  }}
                                />
                              )}

                              {amount !== null &&
                                isBelowThreshold &&
                                !isAboveBudget && (
                                  <WarningIcon
                                    fontSize="small"
                                    style={{
                                      marginRight: "auto",
                                      color: "orange",
                                    }}
                                  />
                                )}

                              {formattedAmount}
                            </div>
                          );
                        },
                      };
                    }),
                    {
                      field: "budgetTotal",
                      headerAlign: "center",
                      headerName: "Σύνoλο Προϋπ.",
                      flex: 1,
                      minWidth: 180,
                      maxWidth: 200,
                      renderCell: (params) => {
                        const value = parseFloat(params.value);
                        if (!value) return null;

                        const formattedAmount = new Intl.NumberFormat("en-IE", {
                          style: "currency",
                          currency: "EUR",
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        }).format(value);

                        return (
                          <span
                            style={{
                              display: "inline-block",
                              textAlign: "right",
                              width: "100%",
                            }}
                          >
                            {formattedAmount}
                          </span>
                        );
                      },
                    },
                    {
                      field: "totalItemActualCost",
                      headerAlign: "center",
                      headerName: "Σύνολο Δαπάνης",
                      flex: 1,
                      minWidth: 150,
                      maxWidth: 200,
                      renderCell: (params) => {
                        const formatted = params?.value
                          ? new Intl.NumberFormat("en-IE", {
                              style: "currency",
                              currency: "EUR",
                              minimumFractionDigits: 2,
                              maximumFractionDigits: 2,
                            }).format(params?.value)
                          : null;
                        return (
                          <span
                            style={{
                              display: "inline-block",
                              textAlign: "right",
                              width: "100%",
                            }}
                          >
                            {formatted}
                          </span>
                        );
                      },
                    },
                    // {
                    //   field: 'totalItemBudget',
                    //   headerAlign: 'center',
                    //   headerName: 'Σύνολο Πρ. Δαπάνης',
                    //   flex: 1,
                    //   minWidth: 150,
                    //   maxWidth: 200,
                    //   renderCell: (params) => {
                    //     const formatted = params?.row?.totalItemBudget ? (new Intl.NumberFormat('en-IE', {
                    //       style: 'currency',
                    //       currency: 'EUR',
                    //       minimumFractionDigits: 2,
                    //       maximumFractionDigits: 2,
                    //     }).format(params.row.average)) : null;
                    //     return (
                    //       <span style={{ display: 'inline-block', textAlign: 'right', width: '100%' }}>
                    //         {formatted}
                    //       </span>
                    //     );
                    //   }
                    // },
                  ]}
                  onCellClick={handleCellClick}
                  initialState={{
                    sorting: {
                      sortModel: [{ field: "id", sort: "asc" }], // Default sorting by pci_aa
                    },
                  }}
                  columnVisibilityModel={{
                    id: false,
                  }}
                  style={{ overflow: "hidden", width: "100%" }}
                />
              )}
            </Paper>
          </Box>
        </Grid>
        {setSelectedItem && selectedColumn > 0 && (
          <Drawer
            anchor="right"
            open={Boolean(selectedItem)}
            onClose={handlePanelClose}
            PaperProps={{
              style: { width: "400px" },
            }}
          >
            <ProductionStatusDetails
              item={selectedItem}
              column={selectedColumn}
              details={itemsDetails}
              itemName={selectedItemName}
              budget={selectedBudget}
              threshold={threshold}
              onClose={handlePanelClose}
            />
          </Drawer>
        )}
      </Grid>
    </Box>
  );
};

export default EpisodeGrid;
